import { Country, State, City } from '../../../services/locationAPI';

/**
 * Filter items based on search term
 */
export const filterItems = <T extends Country | State | City>(
  items: T[],
  searchTerm: string,
  getItemLabel: (item: T) => string = (item) => item.name
): T[] => {
  if (!searchTerm.trim()) return items;
  
  const term = searchTerm.toLowerCase();
  return items.filter(item => 
    getItemLabel(item).toLowerCase().includes(term)
  );
};

/**
 * Calculate dropdown position to avoid viewport overflow
 */
export const calculateDropdownPosition = (
  triggerElement: HTMLElement
): { top: number; left: number; maxHeight: number } => {
  const rect = triggerElement.getBoundingClientRect();
  const viewportHeight = window.innerHeight;
  const viewportWidth = window.innerWidth;
  
  const spaceBelow = viewportHeight - rect.bottom;
  const spaceAbove = rect.top;
  const maxDropdownHeight = 200;
  
  let top = rect.bottom + window.scrollY;
  let maxHeight = Math.min(maxDropdownHeight, spaceBelow - 10);
  
  // If not enough space below, show above
  if (spaceBelow < maxDropdownHeight && spaceAbove > spaceBelow) {
    top = rect.top + window.scrollY - maxDropdownHeight;
    maxHeight = Math.min(maxDropdownHeight, spaceAbove - 10);
  }
  
  let left = rect.left + window.scrollX;
  
  // Ensure dropdown doesn't overflow right edge
  const dropdownWidth = rect.width;
  if (left + dropdownWidth > viewportWidth) {
    left = viewportWidth - dropdownWidth - 10;
  }
  
  return { top, left, maxHeight };
};

/**
 * Check if element contains another element (for portal click detection)
 */
export const isElementInPortal = (element: Element): boolean => {
  return !!element.closest('.cascading-dropdown-portal');
};

/**
 * Get theme-specific CSS classes
 */
export const getThemeClasses = (theme: 'light' | 'dark') => ({
  container: theme === 'dark' 
    ? 'bg-black bg-opacity-50 text-white' 
    : 'bg-white text-gray-900',
  dropdown: theme === 'dark'
    ? 'bg-gray-800 border-gray-600'
    : 'bg-white border-gray-200',
  item: theme === 'dark'
    ? 'text-white hover:bg-gray-700'
    : 'text-gray-900 hover:bg-gray-100',
  selectedItem: theme === 'dark'
    ? 'bg-gray-700 text-white'
    : 'bg-blue-50 text-blue-900',
  input: theme === 'dark'
    ? 'bg-gray-800 text-white placeholder-gray-400'
    : 'bg-white text-gray-900 placeholder-gray-500',
  loading: theme === 'dark'
    ? 'text-gray-400'
    : 'text-gray-500'
});

/**
 * Validate required selections
 */
export const validateSelections = (
  country: string,
  state: string,
  city: string,
  required: { country?: boolean; state?: boolean; city?: boolean },
  messages: { country: string; state: string; city: string } = {
    country: 'País es requerido',
    state: 'Estado/Provincia es requerido',
    city: 'Ciudad es requerida',
  }
): { [key: string]: string } => {
  const errors: { [key: string]: string } = {};
  
  if (required.country && !country) {
    errors.country = messages.country;
  }
  
  if (required.state && !state) {
    errors.state = messages.state;
  }
  
  if (required.city && !city) {
    errors.city = messages.city;
  }
  
  return errors;
};

/**
 * Find item by name in array
 */
export const findItemByName = <T extends Country | State | City>(
  items: T[],
  name: string
): T | undefined => {
  return items.find(item => item.name === name);
};

/**
 * Get display value for selected item
 */
export const getDisplayValue = (selectedValue: string, placeholder: string): string => {
  return selectedValue || placeholder;
};
